import React, { useCallback, useMemo, useState } from 'react';

import { CtaButton } from '../../../../shared-components/CtaButton/CtaButton';
import { StandardOrgSettingInput } from '../../../../shared-components/StandardSettingInput';
import { ViewContainer } from '../../../../shared-components/View/ViewContainer/ViewContainer';
import { mobxInjectSelect } from '../../../../../common/utils';

import styles from '../../OrganizationSettings.module.css';
import { TabProps } from '../';
import { HttpsComponent } from './HttpsComponent';
import { LdapsComponent } from './LdapsComponent';
import { SamlComponent } from './SamlComponent';
import { SamlComponent as SamlComponentExtended } from './SamlComponentExtended';
import LoginWorkflowComponent from './LoginWorkflowComponent';
import { OidcComponent } from './oidc/OidcComponent';
import { thunk, useAppDispatch, useAppSelector } from 'redux-stores';

const allAuthOptions = [
  {
    label: 'HTTPS (basic)',
    value: 'basic',
  },
  {
    label: 'HTTPS (NTLM)',
    value: 'ntlm',
  },
  {
    label: 'LDAPS',
    value: 'ldap',
  },
  {
    label: 'SAML',
    value: 'saml',
  },
  {
    label: 'OIDC',
    value: 'oidc',
  },
  {
    label: 'None',
    value: 'native',
  },
];

const allAuthOptionsWithoutOidc = allAuthOptions.filter((o) => o.value !== 'oidc');

const samlOnlyOptions = {
  label: 'SAML',
  value: 'saml',
};

const oidcOptions = {
  label: 'OIDC',
  value: 'oidc',
};

const noneOption = {
  label: 'None',
  value: 'native',
};

type MobxProps = {
  isProductSettingsAdmin: boolean;
  isProductSuperAdmin: boolean;
  allowSelfServiceSamlConfig: boolean;
  allowOidcConfig: boolean;
};

const AuthComponents = ({
  onClose,
  isProductSettingsAdmin,
  isProductSuperAdmin,
  allowSelfServiceSamlConfig,
  allowOidcConfig,
}: TabProps & MobxProps) => {
  const dispatch = useAppDispatch();
  const authSetting = useAppSelector((state) => state.orgSettings.settings.authType ?? 'native');
  const isOrgAdmin = useAppSelector((state) => state.admin.permissions.includes('admin'));
  const [formIsValid, setFormIsValid] = useState(true);

  // TT admins see all options gated by feature flags; org admins see a subset
  const isTtAdmin = isProductSettingsAdmin || isProductSuperAdmin;

  const authOptions = useMemo(() => {
    if (isTtAdmin) {
      return allowOidcConfig ? allAuthOptions : allAuthOptionsWithoutOidc; // If FF is off, no users must be abel to see the option
    }

    if (isOrgAdmin && allowOidcConfig && allowSelfServiceSamlConfig) {
      return [oidcOptions, samlOnlyOptions, noneOption];
    }

    if (isOrgAdmin && allowOidcConfig) {
      return [oidcOptions, noneOption];
    }

    // Only show SAML options to organization admins when self-service SAML config is enabled
    if (isOrgAdmin && allowSelfServiceSamlConfig) {
      return [samlOnlyOptions, noneOption];
    }

    // Fallback (shouldn't happen since tab access is restricted)
    return [];
  }, [isTtAdmin, isOrgAdmin, allowSelfServiceSamlConfig, allowOidcConfig]);

  const onSave = useCallback(async () => {
    await thunk.saveSettings({ dispatch, location: 'authentication' });
  }, [dispatch]);

  let childComponentFragment = <></>;
  switch (authSetting) {
    case 'saml':
      childComponentFragment = allowSelfServiceSamlConfig ? (
        <SamlComponentExtended />
      ) : (
        <SamlComponent />
      );
      break;
    case 'ldap':
      childComponentFragment = <LdapsComponent />;
      break;
    case 'native':
      childComponentFragment = <LoginWorkflowComponent isOTPLoginDisabled={true} />;
      break;
    case 'basic':
    case 'ntlm':
      childComponentFragment = <HttpsComponent />;
      break;
    case 'oidc':
      childComponentFragment = <OidcComponent setFormIsValid={setFormIsValid} />;
      break;
  }

  return (
    <ViewContainer
      {...{
        header: 'Authentication',
        subHeader:
          'Set up and manage this organization with external provisioning and authentication.',
        onClose,
      }}
    >
      <div className={styles.flexListWithDirection}>
        <div className={styles.flexListStyleWithPadding}>
          <StandardOrgSettingInput type="radio" options={authOptions} settingName="authType" />
        </div>
        {childComponentFragment}
        <div>
          <CtaButton primary label="Save Auth Settings" onClick={onSave} disabled={!formIsValid} />
        </div>
      </div>
    </ViewContainer>
  );
};

export default mobxInjectSelect<TabProps, MobxProps>({
  messengerStore: [
    'isProductSettingsAdmin',
    'isProductSuperAdmin',
    'allowSelfServiceSamlConfig',
    'allowOidcConfig',
  ],
})(AuthComponents);
